import { nanoid } from 'nanoid';
import { objectArrayToStringArray } from '@printdeal/transformers';
import { getLinkedServices } from '@printdeal/business-logic';
// eslint-disable-next-line import/no-unresolved
import { ErrorManagement } from '@printdeal/error-management/react';
import { getConfiguration } from '../../helpers/Configuration';
import { getConfigPerCombo, countCombosWithAndWithoutQuantity } from './ProductConfigurationHelper';

class CartRequestDataBuilder {
  /**
   * @param {import('../../types/product/productDetailPage').ValueOrRangeAttribute[]} chosenAttributes
   * @return {Record<string, string>}
   */
  buildUserSelection(chosenAttributes) {
    const filteredAttributes = chosenAttributes.filter((attribute) =>
      !(attribute.productData.pimAttributeName === 'Production Time' && attribute.value === 'null'));

    return filteredAttributes.reduce((acc, { productData, value }) => {
      acc[productData.pimAttributeName] = value;
      return acc;
    }, {});
  }

  buildProductDescription = (attributes) => {
    const translation = Object.values(attributes).filter(
      (attribute) => (typeof attribute.value !== 'string' || !attribute.value.startsWith('No ')) && !attribute.name.includes('Quantity'),
    ).map((attribute) => {
      const { value, attributeValues, productData } = attribute;
      const attributeValue = attributeValues && attributeValues[value] ? attributeValues[value] : {};
      const valueAlias = attributeValue?.attributeValueAlias || attributeValue?.name || value;
      return `${productData.attributeAlias || productData.pimAttributeName}: ${valueAlias}`;
    }).join(', ');

    /* set translations in the current locale for design info, so the customer knows the productInfo.
     * This is for display only till we have a better solution.
     * On the lineItem it will be overwritten so it's translatable.
     */
    return getConfiguration().locales.reduce((acc, locale) => {
      acc[locale.substring(0, 2)] = translation;
      return acc;
    }, {});
  };

  /**
   * @param {object} params
   * @param {import('../../types/product/productDetailPage').IProductPage} params.productPage
   * @param {Record<string, string>} params.userSelection
   * @param {string=} params.productDescription
   * @param {import('../../types/product/productDetailPage').SingleAttributes=} params.singleAttributes
   * @param {string=} params.groupId
   * @param {boolean=} params.isGroupLead
   * @param {string=} params.multipleDesignsGroup
   * @param {string=} params.documentReferenceUrl
   * @param {import('../../types/file-delivery/FileDelivery').IAlternativeUpload[]=} params.alternativelyUploadedFiles
   * @param {string=} params.editorType
   * @param {string[]=} params.services
   * @returns {{
   *   sku: string
   *   productPageId: string
   *   custom: {
   *     type: {
   *       key: string
   *     },
   *     fields: {
   *       userSelection: Record<string, string>
   *       contentfulPageId: string
   *       merchandisingProductName: string
   *       merchandisingProductCategory: string
   *       productDescription?: string
   *       reviewGroup?: string
   *       fulfillmentGroup?: string
   *       isReviewGroupLead?: boolean
   *     }
   *   }
   * }}
   */

  getLineItemShape({
    productPage,
    userSelection,
    productDescription = undefined,
    groupId = undefined,
    isGroupLead = false,
    multipleDesignsGroup = undefined,
    documentReferenceUrl = undefined,
    alternativelyUploadedFiles = undefined,
    editorType = undefined,
    services = undefined,
    variantSku,
  }) {
    const product = productPage?.resolvedSelectors?.product || productPage?.selectorExperiment?.control?.product;

    if (!product) {
      ErrorManagement.exception({
        exception: 'Product not found while trying to generate payload addLineItem.',
        extra: {
          productPageId: productPage.contentful_id,
          variantSku,
        },
        tags: {
          fileLocation: 'cartRequestDataBuilder',
          featureFunction: 'getLineItemShape',
        },
      });

      throw new Error('Product not found while trying to generate payload addLineItem.');
    }

    return {
      sku: variantSku,
      productPageProductId: product?.key,
      ...(services?.length && services.length > 0 && { services }),
      custom: {
        type: {
          key: 'lineItemCustomFields',
        },
        fields: {
          userSelection: objectArrayToStringArray(userSelection),
          contentfulPageId: productPage.contentful_id,
          merchandisingProductName: product?.productName,
          merchandisingProductCategory: productPage?.productCategory?.categoryName || '',
          ...(productDescription && { productDescription }),
          ...(groupId && { reviewGroup: groupId, fulfillmentGroup: groupId }),
          ...(multipleDesignsGroup && { multipleDesignsGroup }),
          ...(isGroupLead && { isReviewGroupLead: isGroupLead }),
          ...(documentReferenceUrl && { mcpDocumentsSource: [`DocumentReferenceUrl:${documentReferenceUrl}`] }),
          ...(editorType && { editorType }),
          ...(alternativelyUploadedFiles && {
            userUploadsUrl: alternativelyUploadedFiles.map(({
              id, uploadTitle, urls, uploadTimestamp,
            }) => ([id, uploadTitle, urls.originalUrl, uploadTimestamp].join('::'))),
            userUploadComplete: true,
          }),
        },
      },
    };
  }

  buildLineItemsFromProduct({
    productConfig,
    productPage,
    userSelection,
    groupSelection,
    documentReferenceUrl,
    alternativelyUploadedFiles,
    editorType,
    services,
    variantSku,
  }) {
    const product = productPage?.resolvedSelectors?.product || productPage?.selectorExperiment?.control?.product;

    if (!product) {
      ErrorManagement.exception({
        exception: 'Product not found while trying to generate payload addLineItem.',
        extra: {
          productPageId: productPage.contentful_id,
          variantSku,
        },
        tags: {
          fileLocation: 'cartRequestDataBuilder',
          featureFunction: 'buildLineItemsFromProduct',
        },
      });

      throw new Error('Product not found while trying to generate payload addLineItem.');
    }

    /**
     * When there is a key-value based user selection, simply shape the lineItem for the add-to-cart request.
    */
    if (userSelection) {
      // When there is a group..
      if (groupSelection?.length) {
        // ..map over the line items and extend the user selection with the group selection
        const groupUserSelections = groupSelection.map((groupAttribute) => ({
          ...userSelection,
          Quantity: groupAttribute.quantity,
          ...groupAttribute.attributes,
        }));

        // ..and build a lineItem shape for each user selection
        const groupId = nanoid();
        return groupUserSelections.map((groupUserSelection, index) => this.getLineItemShape({
          productPage,
          userSelection: groupUserSelection,
          groupId,
          isGroupLead: Boolean(index === 0),
          documentReferenceUrl,
          alternativelyUploadedFiles,
          editorType,
          variantSku,
        }));
      }
      // ..otherwise just build a single lineItem shape with the user selection
      return [this.getLineItemShape({
        productPage,
        userSelection,
        documentReferenceUrl,
        alternativelyUploadedFiles,
        editorType,
        variantSku,
      })];
    }

    const {
      singleAttributes, comboAttributes: { attributes: attributesForCombos, combos = [] },
    } = productConfig;

    if (Object.values(attributesForCombos).length === 0) {
      // We don't have attributesForCombos at all. We're only going to add a single lineItem.
      const allAttributesAsArray = Object.values(singleAttributes);
      const productDescription = this.buildProductDescription(singleAttributes);
      return [this.getLineItemShape({
        productPage,
        userSelection: this.buildUserSelection(allAttributesAsArray),
        productDescription,
        editorType,
        services,
        variantSku,
      })];
    }

    // Get the combo statistics (how many have quantities, how many don't) if there are combos.
    const comboStatistics = countCombosWithAndWithoutQuantity(combos);

    if (comboStatistics.withQuantity.length === 0) {
      // Something has gone horribly wrong. We should have combo quantities, but we don't! Abort mission!
      ErrorManagement.exception({
        exception: 'No valid comboQuantities while trying to generate payload addLineItem.',
        extra: {
          productKey: product?.key,
          variantSku,
          productConfig: JSON.stringify(productConfig),
        },
        tags: {
          fileLocation: 'cartRequestDataBuilder',
          featureFunction: 'buildLineItemsFromProduct',
        },
        fingerprint: ['pdp-addLineItemShape-comboQuantitiesMissing'],
      });

      throw new Error('No valid comboQuantities while trying to generate payload addLineItem.');
    }

    // Get the lineItem for each combo.
    const groupedLineItems = getConfigPerCombo(
      comboStatistics.withQuantity,
      attributesForCombos,
      singleAttributes,
    );

    const groupId = nanoid();
    return groupedLineItems.map((allAttributesForCombo, index) => {
      const allAttributesAsArray = Object.values(allAttributesForCombo);
      const productDescription = this.buildProductDescription(allAttributesForCombo);
      return this.getLineItemShape({
        productPage,
        userSelection: this.buildUserSelection(allAttributesAsArray),
        productDescription,
        groupId,
        isGroupLead: Boolean(index === 0),
        editorType,
        services,
        variantSku,
      });
    });
  }

  /**
   * @param lineItem
   * @return {{
   *   productPageProductId,
   *   custom: {
   *     type: {key: string},
   *     fields: {
   *       merchandisingProductName: *,
   *       userSelection: (string)[],
   *       merchandisingProductCategory: string,
   *       productDescription: *
   *     }
   *   },
   *   sku: string
   * }[]}
   */
  buildLineItemsFromExistingLineItem(lineItem, cart) {
    let lineItems;

    if (lineItem.custom?.fields.isReviewGroupLead) {
      // Get all lineItems from the group
      lineItems = cart.lineItems
        .filter((item) => item.custom.fields?.reviewGroup === lineItem.custom.fields?.reviewGroup);
    } else {
      lineItems = [lineItem, ...(lineItem.siblings || [])];
    }

    return lineItems.map((item) => {
      const {
        variant,
        quantity,
        custom: {
          fields: {
            merchandisingProductCategory,
            merchandisingProductName,
            productKey,
            productDescription,
            userSelection,
            contentfulPageId,
            localizedProductName,
            isReviewGroupLead,
            reviewGroup,
            fulfillmentGroup,
            reorderOriginalLineItemNumber,
          },
        },
      } = item;

      // Copy also the services
      let servicesKeys = [];
      const servicesLineItems = getLinkedServices(lineItem.id, cart);
      if (servicesLineItems) {
        servicesKeys = servicesLineItems.reduce((keys, serviceLineItem) => {
          keys.push(serviceLineItem.custom.fields?.serviceKey);
          return keys;
        }, []);
      }

      return {
        quantity,
        sku: variant.sku,
        productPageProductId: productKey,
        custom: {
          type: {
            key: 'lineItemCustomFields',
          },
          fields: {
            productDescription,
            merchandisingProductName,
            merchandisingProductCategory,
            contentfulPageId,
            userSelection,
            localizedProductName,
            isReviewGroupLead,
            reviewGroup,
            fulfillmentGroup,
            reorderOriginalLineItemNumber,
          },
        },
        ...(servicesKeys.length > 0 && { services: servicesKeys }),
      };
    });
  }
}

export { CartRequestDataBuilder };
